import { Button, Dropdown, MenuProps, Row, Tooltip } from "antd";
import { EditOutlined, DollarOutlined, EllipsisOutlined } from "@ant-design/icons";
import { usePermissions } from "../../../../../../../../../lib/hooks/usePermissions";
import { useCallback, useMemo, MouseEvent } from "react";
import { IImpoundsList } from "../../../../../../../../../types/Impounds";
import { useToggleShortage } from "../hooks/useToggleShortage";

const TAB_KEYS = {
  PAYMENTS_FROM_IMPOUND: "paymentsFromImpound",
  ACTIVITY_DETAILS: "activitydetails",
  DETAILS: "details"
};

type TableActionsProps = {
  isCreateBtnDisabled: boolean;
  record: IImpoundsList;
  onDeleteBtnClick: (id: string) => void;
  onOpenBtnClick: (id: string, activeKey: string, value?: boolean) => void;
  onCreateImpoundDisbBtnClick: (entry: IImpoundsList) => void;
  reloadData: () => void;
};

export const TableActions = ({
  isCreateBtnDisabled,
  record,
  onDeleteBtnClick,
  onOpenBtnClick,
  onCreateImpoundDisbBtnClick,
  reloadData
}: TableActionsProps) => {
  const { hasAccess } = usePermissions();

  const { id, showShortageForUsers } = record;

  const { toggleShortage, isLoading } = useToggleShortage(record, reloadData);

  const onDelete = useCallback(
    (e: MouseEvent<HTMLSpanElement>) => {
      e.stopPropagation();
      onDeleteBtnClick(id);
    },
    [onDeleteBtnClick, id]
  );

  const viewPayments = useCallback(
    (e: MouseEvent<HTMLSpanElement>) => {
      e.stopPropagation();
      onOpenBtnClick(id, TAB_KEYS.PAYMENTS_FROM_IMPOUND);
    },
    [id, onOpenBtnClick]
  );

  const viewActivities = useCallback(
    (e: MouseEvent<HTMLSpanElement>) => {
      e.stopPropagation();
      onOpenBtnClick(id, TAB_KEYS.ACTIVITY_DETAILS);
    },
    [id, onOpenBtnClick]
  );

  const createDisbursement = useCallback(
    (e: MouseEvent<HTMLSpanElement>) => {
      e.stopPropagation();
      onCreateImpoundDisbBtnClick(record);
    },
    [onCreateImpoundDisbBtnClick, record]
  );

  const payFormImpound = useCallback(
    (e: MouseEvent<HTMLSpanElement>) => {
      e.stopPropagation();
      onOpenBtnClick(id, TAB_KEYS.PAYMENTS_FROM_IMPOUND, true);
    },
    [id, onOpenBtnClick]
  );

  const editImpund = useCallback(
    (e: MouseEvent<HTMLSpanElement>) => {
      e.stopPropagation();
      onOpenBtnClick(id, TAB_KEYS.DETAILS);
    },
    [id, onOpenBtnClick]
  );

  const items: MenuProps["items"] = useMemo(() => {
    const values: MenuProps["items"] = [
      {
        key: "1",
        label: <div onClick={viewPayments}>View Payments From Impound</div>
      },
      {
        key: "2",
        label: <div onClick={viewActivities}>View Activity Details</div>
      }
    ];

    hasAccess("Impounds", "Modify") &&
      values.push({
        key: "5",
        className: "create-impound-disb-btn pa-0",
        label: (
          <Button type="text" className="px-3 py-1 h-auto" disabled={isLoading} onClick={toggleShortage}>
            {showShortageForUsers ? "Hide" : "Show"} Impound Shortage Notification
          </Button>
        )
      });

    hasAccess("ContractDisbursements", "Add") &&
      values.push({
        key: "4",
        className: "create-impound-disb-btn pa-0",
        label: (
          <Button disabled={isCreateBtnDisabled} type="text" className="px-3 py-1 h-auto" onClick={createDisbursement}>
            Create Disbursement
          </Button>
        )
      });

    hasAccess("Impounds", "Delete") &&
      values.push({
        key: "3",
        label: <div onClick={onDelete}>Delete</div>
      });

    return values;
  }, [
    viewPayments,
    viewActivities,
    hasAccess,
    isLoading,
    toggleShortage,
    showShortageForUsers,
    isCreateBtnDisabled,
    createDisbursement,
    onDelete
  ]);

  return (
    <Row className="justify-end ant-table-actions mr-1">
      {hasAccess("ImpoundDistributions", "Add") && (
        <Tooltip title="Pay From Impound" trigger={"hover"}>
          <DollarOutlined className="ant-table-btn" onClick={payFormImpound} />
        </Tooltip>
      )}

      {hasAccess("Impounds", "Modify") && <EditOutlined className="ant-table-btn" onClick={editImpund} />}

      <Dropdown trigger={["click"]} menu={{ items }}>
        <EllipsisOutlined className="ant-table-btn" onClick={(e) => e.stopPropagation()} />
      </Dropdown>
    </Row>
  );
};
